/* Mega menu updated to the restrained Buitn Planten catalogue styling.
   Updated: Mega menu product cards now use the category card style with rounded-xl, hover ring, aspect-[4/3] image, and white label bar.
   Updated: batched product image + variant lookups into two queries instead of N+1 per product,
     and added lazy/async decoding + intrinsic dimensions on product images. */
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getProductImageUrl } from '@/lib/image-url';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Package, ArrowRight } from 'lucide-react';

interface ProductImage { id: string; storage_path: string; display_order: number; is_primary: boolean; alt_text?: string; url?: string; }
interface Product { id: string; name: string; scientific_name: string; base_slug: string; geslacht: string; is_simple_product: boolean; category_slug?: string; subcategory_id?: string | null; images?: ProductImage[]; variant_count?: number; single_variant_slug?: string | null; }
interface Subcategory { id: string; name: string; slug: string; display_order: number; }
interface GroupedProducts { [subcategoryName: string]: Product[]; }

interface AssortimentMegaMenuProps { isOpen: boolean; activeCategory?: string | null; }

export default function AssortimentMegaMenu({ isOpen, activeCategory }: AssortimentMegaMenuProps) {
  const [groupedProducts, setGroupedProducts] = useState<GroupedProducts>({});
  const [loading, setLoading] = useState(true);
  const [hoveredSubcategory, setHoveredSubcategory] = useState<string | null>(null);

  useEffect(() => {
    if (activeCategory) { setHoveredSubcategory(null); setGroupedProducts({}); setLoading(true); fetchProducts(activeCategory); }
    else { setGroupedProducts({}); setHoveredSubcategory(null); setLoading(false); }
  }, [activeCategory]);

  useEffect(() => {
    if (!isOpen) setHoveredSubcategory(null);
  }, [isOpen]);

  const fetchProducts = async (categorySlug: string) => {
    try {
      setLoading(true);
      const { data: catData } = await supabase.from('product_categories').select('id').eq('slug', categorySlug).single();
      if (!catData) { setGroupedProducts({}); setLoading(false); return; }

      const { data: subcategories, error: subError } = await supabase
        .from('product_subcategories').select('id, name, slug, display_order').eq('category_id', catData.id).eq('active', true).order('display_order', { ascending: true });
      if (subError) throw subError;

      const { data: products, error } = await supabase
        .from('products').select('id, name, scientific_name, base_slug, geslacht, is_simple_product, subcategory_id')
        .eq('active', true).eq('category_id', catData.id).order('name', { ascending: true });
      if (error) throw error;

      const productIds = (products || []).map((p: any) => p.id);

      const [imagesResult, variantsResult] = await Promise.all([
        productIds.length
          ? supabase
              .from('product_images')
              .select('product_id, storage_path, display_order, is_primary, alt_text, uses_new_bucket')
              .in('product_id', productIds)
              .is('variant_id', null)
              .order('display_order', { ascending: true })
          : Promise.resolve({ data: [] as any[] }),
        productIds.length
          ? supabase
              .from('product_variants')
              .select('product_id, slug')
              .in('product_id', productIds)
              .eq('active', true)
          : Promise.resolve({ data: [] as any[] }),
      ]);

      const imagesByProduct = new Map<string, any[]>();
      (imagesResult.data || []).forEach((img: any) => {
        if (!imagesByProduct.has(img.product_id)) imagesByProduct.set(img.product_id, []);
        imagesByProduct.get(img.product_id)!.push(img);
      });

      const variantsByProduct = new Map<string, any[]>();
      (variantsResult.data || []).forEach((v: any) => {
        if (!variantsByProduct.has(v.product_id)) variantsByProduct.set(v.product_id, []);
        variantsByProduct.get(v.product_id)!.push(v);
      });

      const productsWithDetails: Product[] = (products || []).map((product: any) => {
        const productImages = (imagesByProduct.get(product.id) || []).slice(0, 1);
        const imagesWithUrls = productImages.map((img: any) => ({ ...img, url: getProductImageUrl(img.storage_path, img.uses_new_bucket) }));
        let variantCount = 0;
        let singleVariantSlug: string | null = null;
        if (!product.is_simple_product) {
          const variants = variantsByProduct.get(product.id) || [];
          variantCount = variants.length;
          singleVariantSlug = variantCount === 1 ? variants[0].slug : null;
        }
        return {
          id: product.id,
          name: product.name,
          scientific_name: product.scientific_name || '',
          base_slug: product.base_slug,
          geslacht: product.geslacht || '',
          is_simple_product: product.is_simple_product || false,
          category_slug: categorySlug,
          subcategory_id: product.subcategory_id,
          images: imagesWithUrls,
          variant_count: variantCount,
          single_variant_slug: singleVariantSlug,
        };
      });

      const subcategoryMap = new Map<string, string>();
      (subcategories || []).forEach((sub: Subcategory) => subcategoryMap.set(sub.id, sub.name));
      const orderedSubcategories = (subcategories || []).sort((a: Subcategory, b: Subcategory) => a.display_order - b.display_order);
      const grouped: GroupedProducts = {};
      orderedSubcategories.forEach((sub: Subcategory) => { grouped[sub.name] = []; });
      productsWithDetails.forEach((product: Product) => {
        let groupName = 'Overig';
        if (product.subcategory_id && subcategoryMap.has(product.subcategory_id)) groupName = subcategoryMap.get(product.subcategory_id)!;
        if (!grouped[groupName]) grouped[groupName] = [];
        grouped[groupName].push(product);
      });
      Object.keys(grouped).forEach((key) => { if (grouped[key].length === 0) delete grouped[key]; });
      setGroupedProducts(grouped);
      const firstSub = Object.keys(grouped)[0] || null;
      setHoveredSubcategory(firstSub);
    } catch (error) { console.error('Error fetching products:', error); setGroupedProducts({}); }
    finally { setLoading(false); }
  };

  const subcategoryList = Object.keys(groupedProducts);
  const selectedProducts = hoveredSubcategory ? (groupedProducts[hoveredSubcategory] || []) : [];
  if (isOpen && !loading && subcategoryList.length === 0) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="absolute left-0 right-0 top-full bg-[#fcfaf6] shadow-[0_12px_40px_rgba(30,43,34,0.12)] border-t border-[#e7e1d6]"
          style={{ maxHeight: '70vh' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-[#5a6552]">Laden...</div>
          ) : (
            <div className="flex w-full" style={{ maxHeight: '70vh' }}>
              <div className="w-56 border-r border-[#e7e1d6] bg-[#e7e1d6] overflow-y-auto flex-shrink-0 py-3">
                {subcategoryList.map((subName) => (
                  <button key={subName} onMouseEnter={() => setHoveredSubcategory(subName)}
                    className={`w-full text-left px-6 py-3.5 transition-colors duration-150 flex items-center justify-between group ${hoveredSubcategory === subName ? 'bg-[#fcfaf6] text-[#1e2b22] border-l-2 border-[#1e2b22]' : 'text-[#5a6552] hover:bg-[#fcfaf6] hover:text-[#1e2b22]'}`}>
                    <span className="text-sm font-medium">{subName}</span>
                    <ChevronRight className={`h-4 w-4 transition-all ${hoveredSubcategory === subName ? 'opacity-100 translate-x-0.5 text-[#1e2b22]' : 'opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5'}`} />
                  </button>
                ))}
              </div>
              <div className="flex-1 overflow-y-auto p-8">
                <AnimatePresence mode="wait">
                  {hoveredSubcategory && (
                    <motion.div key={hoveredSubcategory} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.18 }}>
                      <h3 className="font-playfair text-2xl text-[#1e2b22] mb-6">{hoveredSubcategory}</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {selectedProducts.map((product) => {
                          let href = `/${product.base_slug}`;
                          if (product.is_simple_product) href = `/${product.base_slug}`;
                          else if (product.variant_count === 1 && product.single_variant_slug) href = `/${product.single_variant_slug}`;
                          return (
                            <a
                              key={product.id}
                              href={href}
                              className="group flex-shrink-0 w-[140px] sm:w-auto flex flex-col rounded-xl overflow-hidden border border-transparent hover:ring-2 hover:ring-buitn-bosgroen transition-all duration-200 bg-white"
                              style={{ ['--tw-ring-offset-color' as any]: '#fcfaf6', ['--tw-ring-offset-width' as any]: '3px' }}
                            >
                              <div className="aspect-[4/3] overflow-hidden">
                                {product.images && product.images.length > 0 ? (
                                  <img
                                    src={product.images[0].url || ''}
                                    alt={product.images[0].alt_text || product.name}
                                    width={300}
                                    height={225}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  />
                                ) : (
                                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#e7e1d6]">
                                    <Package className="h-16 w-16 text-[#cfcac2]" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 px-3 py-2.5 bg-white border-t border-gray-100 flex items-center justify-between gap-1">
                                <span className="text-xs sm:text-[13px] font-medium text-buitn-bosgroen leading-snug break-words">
                                  {product.name}
                                </span>
                                <ArrowRight className="w-3 h-3 flex-shrink-0 text-buitn-bosgroen/50 group-hover:text-buitn-bosgroen transition-colors" />
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
